import React from 'react';
import { X } from 'lucide-react';

const LegalModal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
    <div className="absolute inset-0 bg-[rgba(0,0,0,0.45)] backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-canvas border border-hairline rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg flex flex-col max-h-[85vh]">
      <div className="flex items-center justify-between px-6 py-4 border-b border-hairline shrink-0">
        <h2 className="font-black text-ink text-lg">{title}</h2>
        <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="overflow-y-auto px-6 py-5 text-[13px] text-ink-2 leading-relaxed space-y-5">
        {children}
      </div>
    </div>
  </div>
);

export const TermsOfService = ({ onClose }: { onClose: () => void }) => (
  <LegalModal title="Terms of Service" onClose={onClose}>
    <p className="text-ink-2 text-[11px] uppercase tracking-widest font-semibold">Effective June 26, 2026</p>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">1. Acceptance</p>
      <p>By creating an account or using Dugout, you agree to these Terms. If you do not agree, do not use the app. You must be at least 18 years old to create an account.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">2. What Dugout Is</p>
      <p>Dugout is a team management app designed for youth sports coaches. It helps you manage rosters, lineups, pitch counts, game stats, and player evaluations. It is not a medical, legal, or official governing-body compliance tool.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">3. Your Account</p>
      <p>You sign in using Google. You are responsible for all activity that occurs under your account. Do not share access credentials or allow unauthorized users to access your account.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">4. Player Information</p>
      <p>Dugout is for coaches. Players do not have accounts and do not use the app. As a coach, you may enter basic information about the players on your roster (such as names, positions, and statistics) to manage your team. You're responsible for having the appropriate authority to do so, and for keeping that information accurate and appropriate.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">5. Acceptable Use</p>
      <p>You agree not to use Dugout to: (a) violate any applicable law; (b) upload harmful, offensive, or misleading content; (c) attempt to reverse-engineer, scrape, or interfere with the service; or (d) share your account with others outside your coaching staff.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">6. Intellectual Property</p>
      <p>All software, design, and content in Dugout is owned by Dugout. You are granted a limited, non-exclusive license to use the app for its intended purpose. You may not copy, redistribute, or create derivative works from any part of Dugout.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">7. Disclaimers</p>
      <p>Dugout is provided "as is" without warranties of any kind. We do not guarantee uptime, accuracy of calculations, or that the service will meet every need. Pitch count limits and rest rules displayed in the app are informational. Always verify requirements with your league or governing body.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">8. Limitation of Liability</p>
      <p>To the maximum extent permitted by law, Dugout is not liable for any indirect, incidental, or consequential damages arising from your use of the app. Our total liability for any claim is limited to the amount you paid us in the preceding 12 months (if any).</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">9. Termination</p>
      <p>We may suspend or terminate your account at any time for violation of these Terms or for any other reason with reasonable notice. You may stop using Dugout at any time.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">10. Changes to These Terms</p>
      <p>We may update these Terms from time to time. If we make material changes, we will notify you in-app. Continued use after changes take effect constitutes acceptance of the new Terms.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">11. Accessibility</p>
      <p>We want everyone to be able to use Dugout. We design with accessibility in mind and work toward the WCAG 2.1 AA guidelines. If you encounter a barrier, tell us at <span className="text-accent-ink">team@dugoutbaseball.app</span>. We take it seriously and we'll work to make it right.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">12. Governing Law</p>
      <p>These Terms are governed by the laws of the United States. Any disputes will be resolved in the jurisdiction where Dugout operates.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">13. Contact</p>
      <p>Questions about these Terms? Email us at <span className="text-accent-ink">team@dugoutbaseball.app</span>.</p>
    </div>
  </LegalModal>
);

export const PrivacyPolicy = ({ onClose }: { onClose: () => void }) => (
  <LegalModal title="Privacy Policy" onClose={onClose}>
    <p className="text-ink-2 text-[11px] uppercase tracking-widest font-semibold">Effective June 26, 2026</p>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">1. What We Collect</p>
      <p>When you sign in, we receive your name, email address, and profile picture. As you use Dugout, we store the team data you create: your roster, lineups, pitch counts, game stats, and player evaluations.</p>
      <p>Dugout is used by coaches. Players do not have accounts and do not interact with the app. We do not collect information from players, and players cannot sign in, view, or submit anything.</p>
      <p>We do not store payment card information. Subscriptions are handled by Apple or by a third-party payment processor, and your card details stay with them.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">2. How We Use Your Information</p>
      <p>We use your information solely to operate and improve Dugout. This includes: displaying your team and player data, computing lineup and stat analytics, and sending transactional communications (e.g., support replies). We do not use your data for advertising.</p>
      <p>We also record anonymous usage data about how the app is used, such as which screens are opened and which features are completed, so we can find the places Dugout is confusing and fix them. This is product measurement only. It is never linked to a player, never used for advertising, and never shared with other companies or used to track you across other apps or websites.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">3. Data Storage</p>
      <p>Your data is stored in Google Firebase (Firestore), a cloud database operated by Google. Firebase is subject to Google's own privacy and security practices. Data is stored in the United States.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">4. Sharing</p>
      <p>We do not sell, rent, or share your personal data with third parties for their commercial purposes. We may share data with service providers (such as Google Firebase) who help us operate the app under appropriate confidentiality agreements. We may disclose information if required by law.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">5. Players and Children's Privacy</p>
      <p>Dugout is built for adult coaches, and only coaches use it. Players (including children) do not have accounts and never interact with the app. We do not knowingly collect any information directly from a child.</p>
      <p>A coach may enter basic player information, such as a name, position, and on-field statistics, to manage their team. This information is provided by the coach, not the child. As the coach, you're responsible for having the authority to manage your team's information, consistent with your league's rules.</p>
      <p>Dugout is not directed to children and is not designed for children to use. If you believe a player's information has been entered without authorization, email us at <span className="text-accent-ink">team@dugoutbaseball.app</span> and we'll remove it promptly.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">6. Data Retention</p>
      <p>Your data is retained as long as your account is active. If you delete your account or request deletion, we will remove your personal data and team data within 30 days, except where retention is required by law.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">7. Security</p>
      <p>We use industry-standard security measures including Firebase's built-in authentication and Firestore security rules that restrict data access to authorized coaches only. No system is perfectly secure. Use a strong Google account password and enable two-factor authentication on your Google account.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">8. Your Rights</p>
      <p>You may access, correct, or delete your data at any time from within the app settings, or by contacting us. If you are a resident of California or the EU, you may have additional rights under CCPA or GDPR respectively. Contact us and we will respond within 30 days.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">9. Changes to This Policy</p>
      <p>We may update this Privacy Policy from time to time. Material changes will be communicated in-app. The effective date at the top of this page reflects the most recent version.</p>
    </div>

    <div className="space-y-1">
      <p className="font-bold text-ink text-sm">10. Contact</p>
      <p>Privacy questions or data requests: <span className="text-accent-ink">team@dugoutbaseball.app</span>.</p>
    </div>
  </LegalModal>
);
